(() => {
  // Display order only. Shopify line keys, quantities and signed properties stay intact.
  const visitOrders = new Map();
  class AvidCartOrder extends HTMLElement {
    connectedCallback() {
      this.storageKey = `avid.cart-order.v1:${this.dataset.orderOwner || 'guest'}`;
      this.abort?.abort();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.addEventListener('pointerdown', event => this.start(event), options);
      this.addEventListener('pointermove', event => this.move(event), options);
      this.addEventListener('pointerup', () => this.finish(), options);
      this.addEventListener('pointercancel', () => this.finish(true), options);
      this.addEventListener('lostpointercapture', () => this.finish(true), options);
      this.addEventListener('keydown', event => this.keydown(event), options);
      this.addEventListener('dragstart', event => {
        if (event.target.closest('[data-cart-order-handle]')) event.preventDefault();
      }, options);
      this.restore();
      const enabled = this.rows().length > 1;
      this.dataset.orderReady = String(enabled);
      this.querySelectorAll('[data-cart-order-tools], [data-cart-order-handle]').forEach(node => { node.hidden = !enabled; });
    }

    disconnectedCallback() {
      this.finish(true);
      this.abort?.abort();
    }

    rows() { return [...this.querySelectorAll(':scope > [data-cart-order-id]')]; }
    ids() { return this.rows().map(row => row.dataset.cartOrderId); }

    restore() {
      let saved = visitOrders.get(this.storageKey);
      try { saved ||= JSON.parse(localStorage.getItem(this.storageKey) || '[]'); } catch (_) { /* Sorting also works when browser storage is unavailable. */ }
      if (!Array.isArray(saved) || saved.length > 500 || saved.some(id => typeof id !== 'string')) return;
      const rows = this.rows();
      const rank = new Map(saved.map((id, index) => [id, index]));
      rows.sort((a, b) => (rank.get(a.dataset.cartOrderId) ?? Infinity) - (rank.get(b.dataset.cartOrderId) ?? Infinity));
      rows.forEach(row => this.append(row));
      // Drop removed lines, retain quantity/price changes, and append new lines.
      const current = this.ids();
      visitOrders.set(this.storageKey, current);
      try { localStorage.setItem(this.storageKey, JSON.stringify(current)); } catch (_) { /* In-memory order remains available for section refreshes. */ }
    }

    save(row) {
      const ids = this.ids();
      visitOrders.set(this.storageKey, ids);
      let persisted = true;
      try { localStorage.setItem(this.storageKey, JSON.stringify(ids)); } catch (_) { persisted = false; }
      const title = row.querySelector('h2')?.textContent?.trim() || 'Part';
      this.querySelector('[data-cart-order-status]').textContent = `${title} moved to position ${this.rows().indexOf(row) + 1} of ${ids.length}. ${persisted ? 'Order saved on this browser.' : 'Order saved for this visit only; browser storage is unavailable.'}`;
      const note = this.querySelector('[data-cart-order-tools] small');
      if (note) note.textContent = persisted ? 'Order saved on this browser.' : 'Order saved for this visit only.';
    }

    keydown(event) {
      if (event.key === 'Escape' && this.drag) { event.preventDefault(); this.finish(true); return; }
      const handle = event.target.closest('[data-cart-order-handle]');
      if (!handle || handle.disabled || this.drag || !['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const row = handle.closest('[data-cart-order-id]');
      const rows = this.rows();
      const index = rows.indexOf(row);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? rows.length - 1 : Math.max(0, Math.min(rows.length - 1, index + (event.key === 'ArrowUp' ? -1 : 1)));
      if (index === next) return;
      if (next < index) this.insertBefore(row, rows[next]);
      else this.insertBefore(row, rows[next].nextSibling);
      handle.focus({ preventScroll: true });
      row.scrollIntoView({ block: 'nearest' });
      this.save(row);
    }

    start(event) {
      const handle = event.target.closest('[data-cart-order-handle]');
      if (!handle || handle.disabled || !event.isPrimary || event.button !== 0 || this.drag) return;
      const row = handle.closest('[data-cart-order-id]');
      if (row.getAttribute('aria-busy') === 'true') return;
      event.preventDefault();
      handle.focus({ preventScroll: true });
      this.drag = { handle, row, pointerId: event.pointerId, x: event.clientX, y: event.clientY, startY: event.clientY, next: row.nextSibling, original: this.ids(), style: row.getAttribute('style') };
      handle.setPointerCapture(event.pointerId);
    }

    move(event) {
      const drag = this.drag;
      if (!drag || drag.pointerId !== event.pointerId) return;
      drag.y = event.clientY;
      if (!drag.placeholder && Math.hypot(event.clientX - drag.x, event.clientY - drag.startY) < 6) return;
      event.preventDefault();
      if (!drag.placeholder) {
        const box = drag.row.getBoundingClientRect();
        drag.offset = drag.startY - box.top;
        drag.placeholder = document.createElement('div');
        drag.placeholder.className = 'avid-cart-order__placeholder';
        drag.placeholder.style.height = `${box.height}px`;
        drag.placeholder.setAttribute('aria-hidden', 'true');
        this.insertBefore(drag.placeholder, drag.row);
        drag.row.classList.add('is-cart-dragging');
        Object.assign(drag.row.style, { position: 'fixed', width: `${box.width}px`, left: `${box.left}px`, margin: '0', zIndex: '1000' });
        drag.handle.setAttribute('aria-pressed', 'true');
        document.documentElement.classList.add('avid-cart-order-dragging');
        this.tick();
      }
      this.position();
    }

    position() {
      const drag = this.drag;
      if (!drag?.placeholder) return;
      drag.row.style.top = `${drag.y - drag.offset}px`;
      const before = this.rows().find(row => row !== drag.row && drag.y < row.getBoundingClientRect().top + row.getBoundingClientRect().height / 2);
      if (before) this.insertBefore(drag.placeholder, before);
      else this.append(drag.placeholder);
    }

    tick() {
      const drag = this.drag;
      if (!drag?.placeholder) return;
      const edge = 72;
      const amount = drag.y < edge ? -Math.min(18, (edge - drag.y) / 3) : drag.y > window.innerHeight - edge ? Math.min(18, (drag.y - window.innerHeight + edge) / 3) : 0;
      if (amount) { window.scrollBy(0, amount); this.position(); }
      this.frame = requestAnimationFrame(() => this.tick());
    }

    finish(cancel = false) {
      const drag = this.drag;
      if (!drag) return;
      this.drag = null;
      cancelAnimationFrame(this.frame);
      if (drag.placeholder) {
        if (cancel) {
          if (drag.next?.parentNode === this) this.insertBefore(drag.row, drag.next);
          else this.append(drag.row);
          drag.placeholder.remove();
        } else drag.placeholder.replaceWith(drag.row);
        drag.row.classList.remove('is-cart-dragging');
        if (drag.style === null) drag.row.removeAttribute('style');
        else drag.row.setAttribute('style', drag.style);
        document.documentElement.classList.remove('avid-cart-order-dragging');
      }
      drag.handle.removeAttribute('aria-pressed');
      if (drag.handle.hasPointerCapture(drag.pointerId)) drag.handle.releasePointerCapture(drag.pointerId);
      if (this.isConnected) {
        drag.handle.focus({ preventScroll: true });
        if (!cancel && drag.original.join('\n') !== this.ids().join('\n')) this.save(drag.row);
      }
    }
  }
  if (!customElements.get('avid-cart-order')) customElements.define('avid-cart-order', AvidCartOrder);
})();
